import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, getToken, setToken } from './api';

export type PersonaKind = 'vendor' | 'staff';

export interface Persona {
  id: string;
  name: string;
  title: string;
  org: string;
  mdaCode?: string;
  /** Module-scoped role codes across M11/M12/M14 — see lib/roles.ts. Empty for a plain vendor/applicant. */
  roles: string[];
  kind: PersonaKind;
  trustTier?: string;
  /** The JWT `sub` claim this persona's bearer token carries — e.g. an evaluator recognising their own EvaluationPanelMember.evaluatorId row, or a facilitator's own `createdBy`. Not a display field. */
  sub?: string;
  /** The signed-in subject's own NIN — self-service PERSON-mode filings key off this. */
  nin?: string;
  /** Local-dev-only fixed business mandate; see server/staff-access.ts. */
  actingForBusinessId?: string;
}

export interface AuthConfig {
  oneCitizen: boolean;
  devLogin: boolean;
}

interface AuthState {
  persona: Persona | null;
  personas: Persona[];
  config: AuthConfig;
  loading: boolean;
  login: (personaId: string) => Promise<void>;
  /** Store a session issued by the gateway (One Citizen sign-in). */
  completeLogin: (token: string, persona: Persona) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [persona, setPersona] = useState<Persona | null>(() => {
    const raw = localStorage.getItem('trade-portal.persona');
    return raw ? (JSON.parse(raw) as Persona) : null;
  });
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [config, setConfig] = useState<AuthConfig>({ oneCitizen: false, devLogin: true });
  const [loading, setLoading] = useState(true);

  // The public tender/award/contract-register and licence-register/reference
  // browsing screens call the real M11/M12/M14 APIs, which every route sits
  // behind requireAuth+requireTier('T1') for (see server/gateway.ts's long
  // comment on /auth/visitor-token) — so *something* has to hold a bearer
  // token before persona ever exists. This silently mints and holds a bare
  // T1 "visitor" token whenever there is no session token at all yet. It is
  // never treated as a persona: `persona` stays null, nothing renders
  // "signed in as…", and no write path reads it as authorization for
  // anything beyond what any anonymous T1 floor already permits.
  const ensureVisitorToken = useCallback(async () => {
    if (getToken()) return;
    try {
      const r = await api.post<{ token: string }>('/auth/visitor-token');
      setToken(r.token);
    } catch {
      // Public browsing degrades to a per-screen error state; nothing else breaks.
    }
  }, []);

  useEffect(() => {
    Promise.all([
      api.get<{ items: Persona[] }>('/auth/personas').catch(() => ({ items: [] as Persona[] })),
      api.get<AuthConfig>('/auth/config').catch(() => ({ oneCitizen: false, devLogin: true })),
      ensureVisitorToken(),
    ])
      .then(([p, c]) => {
        setPersonas(p.items);
        setConfig(c);
      })
      .finally(() => setLoading(false));
  }, [ensureVisitorToken]);

  const completeLogin = useCallback((token: string, p: Persona) => {
    setToken(token);
    localStorage.setItem('trade-portal.persona', JSON.stringify(p));
    setPersona(p);
  }, []);

  const login = useCallback(
    async (personaId: string) => {
      const r = await api.post<{ token: string; persona: Persona }>('/auth/dev-login', { personaId });
      completeLogin(r.token, r.persona);
    },
    [completeLogin],
  );

  const logout = useCallback(() => {
    setToken(null);
    localStorage.removeItem('trade-portal.persona');
    setPersona(null);
    void ensureVisitorToken();
  }, [ensureVisitorToken]);

  const value = useMemo(
    () => ({ persona, personas, config, loading, login, completeLogin, logout }),
    [persona, personas, config, loading, login, completeLogin, logout],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}
